import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Stack, Paper, CircularProgress } from '@mui/material';
import { useSpring, animated, useTransition } from 'react-spring';
import { Link } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import { motion } from 'framer-motion';
import { useSiteConfig } from '../hooks/useSiteConfig';

const stats = [
  { label: 'Clients', value: '100+' },
  { label: 'Domains', value: '10K+' },
  { label: 'Audits', value: '200K+' },
  { label: 'Emails Delivered', value: '2Bn' },
];

const reverseColor = '#fff';

const AnimatedEmail: React.FC<{ delay: number; duration: number; startX: number; endX: number; startY: number; endY: number; size: number }> = ({ delay, duration, startX, endX, startY, endY, size }) => {
  const animation = useSpring({
    from: { x: startX, y: startY, opacity: 0, rotate: 0 },
    to: { x: endX, y: endY, opacity: 0.15, rotate: 360 },
    config: { duration },
    delay,
    loop: true,
  });

  return (
    <animated.div
      style={{
        position: 'absolute',
        transform: animation.x.to(x => `translate(${x}px, ${animation.y.get()}px) rotate(${animation.rotate.get()}deg)`),
        opacity: animation.opacity,
      }}
    >
      <EmailIcon sx={{ 
        fontSize: size,
        color: reverseColor,
        filter: 'drop-shadow(0 2px 4px rgba(255,255,255,0.2))',
      }} />
    </animated.div>
  );
};

const HeroSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const config = useSiteConfig('home') as {
    brandColor: string;
    currentPage: {
      structure: {
        hero: {
          headlines: string[];
          subheadline: string;
        };
      };
    };
  };
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      if (config && config.currentPage && config.currentPage.structure && config.currentPage.structure.hero) {
        setIsLoading(false);
      }
    } catch (err) {
      setError('Failed to load page configuration');
      setIsLoading(false);
    }
  }, [config]);

  const transitions = useTransition(currentHeadlineIndex, {
    from: { opacity: 0, transform: 'translate3d(0,20px,0)' },
    enter: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    leave: { opacity: 0, transform: 'translate3d(0,-20px,0)' },
    config: {
      tension: 170,
      friction: 26,
      duration: 800
    },
  });

  useEffect(() => {
    if (!isLoading && config.currentPage.structure.hero.headlines) {
      const interval = setInterval(() => {
        setCurrentHeadlineIndex((prevIndex) => (prevIndex + 1) % config.currentPage.structure.hero.headlines.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isLoading, config.currentPage.structure.hero.headlines]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(120deg, ${config.brandColor} 60%, #1e90ff 100%)`,
        }}
      >
        <CircularProgress sx={{ color: reverseColor }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(120deg, ${config.brandColor} 60%, #1e90ff 100%)`,
          color: reverseColor,
        }}
      >
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        px: 2,
        py: 8,
        overflow: 'hidden',
        background: `linear-gradient(120deg, ${config.brandColor} 60%, #1e90ff 100%)`,
        color: reverseColor,
      }}
    >
      {/* Animated Email Icons */}
      {Array.from({ length: 15 }).map((_, index) => (
        <AnimatedEmail
          key={index}
          delay={index * 300}
          duration={4000 + Math.random() * 2000}
          startX={Math.random() * window.innerWidth}
          endX={Math.random() * window.innerWidth}
          startY={Math.random() * window.innerHeight}
          endY={Math.random() * window.innerHeight}
          size={16 + Math.random() * 24}
        />
      ))}

      {/* Floating Email Icons */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <motion.div
            key={`float-${index}`}
            style={{
              position: 'absolute',
              opacity: 0.1,
            }}
            animate={{
              y: ['-100%', '100%'],
              x: `${(index * 16.6) + Math.random() * 5}%`,
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: index * 0.8,
              ease: 'linear',
            }}
          >
            <EmailIcon sx={{ 
              fontSize: 24,
              color: reverseColor,
              filter: 'drop-shadow(0 2px 4px rgba(255,255,255,0.2))',
            }} />
          </motion.div>
        ))}
      </Box>

      {/* Overlay for readability */}
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(11,96,177,0.55)', zIndex: 1 }} />
      <Box sx={{ position: 'relative', zIndex: 2, width: '100%', textAlign: 'center' }}>
        <Box sx={{ minHeight: 80, position: 'relative' }}>
          {transitions((style, index) => (
            <animated.div
              style={{
                position: 'absolute',
                width: '100%',
                ...style,
              }}
            >
              <Typography 
                variant="h2" 
                fontWeight={800} 
                mb={2} 
                sx={{ 
                  color: reverseColor,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  letterSpacing: '-0.5px'
                }}
              >
                {config.currentPage.structure.hero.headlines[index]}
              </Typography>
            </animated.div>
          ))}
        </Box>
        <Typography variant="h6" fontWeight={400} mb={4} sx={{ opacity: 0.96, color: reverseColor }}>
          {config.currentPage.structure.hero.subheadline}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mb={6}>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{
              background: reverseColor,
              color: config.brandColor,
              fontWeight: 700,
              boxShadow: '0 2px 12px 0 rgba(11,96,177,0.12)',
              '&:hover': {
                background: '#e6e6e6',
                color: config.brandColor,
              },
            }}
          >
            Contact Us
          </Button>
        </Stack>
      </Box>

      {/* Stats as individual cards */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: { xs: 16, md: 32 },
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2, md: 3 },
          zIndex: 3,
          pointerEvents: 'none',
          px: { xs: 1, sm: 2 },
        }}
      >
        <Stack 
          direction={{ xs: 'row' }} 
          spacing={{ xs: 1, sm: 2, md: 3 }} 
          sx={{ 
            pointerEvents: 'auto',
            flexWrap: { xs: 'wrap' },
            justifyContent: 'center',
            width: '100%',
            maxWidth: { xs: '100%', md: '1200px' }
          }}
        >
          {stats.map((stat) => (
            <Paper
              key={stat.label}
              elevation={6}
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                minWidth: { xs: 90, sm: 110 },
                maxWidth: { xs: '45%', sm: 'auto' },
                bgcolor: reverseColor,
                color: config.brandColor,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 4px 24px 0 rgba(11,96,177,0.10)',
                border: `2px solid ${config.brandColor}`,
                flex: { xs: '1 1 45%', sm: '0 1 auto' },
              }}
            >
              <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>{stat.value}</Typography>
              <Typography variant="subtitle2" sx={{ opacity: 0.85, fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{stat.label}</Typography>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default HeroSection; 